import { FC, MouseEvent, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography
} from "@mui/material";
import { Send, Undo } from "@mui/icons-material";
import dayjs from "dayjs";
import {
  AssigneeDto,
  OperationalReadiness,
  ReadinessSignatory,
  SignOff
} from "../../../api/Api";
import { Colors } from "../../../theme";
import { ReadinessSignerSelect } from "./readinessSignerSelect";
import {
  isReadinessCompleted,
  isReadinessInProgress,
  READINESS_DEPARTMENTS,
  ReadinessDepartment,
  readinessDepartmentLabel,
  readinessNotNeeded
} from "./operationalReadiness";
import { WithdrawSignOffDialog } from "./withdrawSignOffDialog";

type Props = {
  /** The stored sign-off; operational readiness lives on it. */
  signOff: SignOff | null | undefined;
  currentUserEmail: string;
  entityName?: string | null;
  canManage: boolean;
  saving: boolean;
  onSave: (readiness: OperationalReadiness) => void;
};

type Picked = Record<ReadinessDepartment, AssigneeDto | null>;

const sameEmail = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

const toPicked = (signatories: ReadinessSignatory[]): Picked =>
  Object.fromEntries(
    READINESS_DEPARTMENTS.map(({ key }) => {
      const s = signatories.find((x) => x.department === key);
      return [key, s ? { name: s.name, email: s.email } : null];
    })
  ) as Picked;

const toSignatories = (
  picked: Picked,
  notNeeded: ReadinessDepartment[]
): ReadinessSignatory[] =>
  READINESS_DEPARTMENTS.flatMap(({ key }) => {
    const user = picked[key];
    return user?.email && !notNeeded.includes(key)
      ? [
          {
            department: key,
            name: user.name || user.email,
            email: user.email,
            signed: false
          }
        ]
      : [];
  });

const StatusChip: FC<{ label: string; color?: string }> = ({
  label,
  color
}) => (
  <Chip
    size="small"
    label={label}
    sx={{
      ...(color && { backgroundColor: color, color: "#FFF" }),
      flexShrink: 0,
      marginTop: "2px"
    }}
  />
);

/**
 * Operational readiness (ADHO-551), a tile in the sign-off details grid next to the lifecycle
 * sections. Market Access ticks "Needed" on the tile, which opens a popup to pick one director
 * each from Risk, Treasury and Finance (any of them can be marked not needed) and send it; they
 * sign in parallel from the same popup, and once every picked signer has signed the backend
 * completes it and the VP sign-off can be sent.
 * Editable while the VP sign-off is a draft or completed (backloading), never while VPs sign.
 */
export const OperationalReadinessSection: FC<Props> = ({
  signOff,
  currentUserEmail,
  entityName,
  canManage,
  saving,
  onSave
}) => {
  const readiness = signOff?.operationalReadiness;
  const required = !!readiness?.required;
  const inProgress = isReadinessInProgress(readiness);
  const completed = isReadinessCompleted(readiness);
  // Editable before the VP sign-off is sent, and again once it is completed so readiness can be
  // backloaded onto entities onboarded before it existed. Locked while the VPs are signing.
  const vpStatus = signOff?.status ?? "draft";
  const readinessEditable = vpStatus === "draft" || vpStatus === "completed";
  const configuring = !inProgress && !completed;
  const canConfigure = canManage && readinessEditable && configuring;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [picked, setPicked] = useState<Picked>(() =>
    toPicked(readiness?.signatories ?? [])
  );
  const storedNotNeeded = readinessNotNeeded(readiness);
  const [notNeeded, setNotNeeded] =
    useState<ReadinessDepartment[]>(storedNotNeeded);
  // Nothing stored yet: let each picker propose its department's usual director.
  const prefill = !readiness?.signatories.length;
  // Local until the popup's Save or Send, so ticking "Needed" alone writes nothing.
  const [requiredDraft, setRequiredDraft] = useState(required);
  const [ticked, setTicked] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);

  // An unsaved tick must not survive a withdraw or completion.
  useEffect(() => {
    setTicked(false);
  }, [readiness?.status]);

  if (!required && !(canManage && readinessEditable)) return null;

  const signatories = readiness?.signatories ?? [];
  const signedCount = signatories.filter((s) => s.signed).length;
  const neededDepartments = READINESS_DEPARTMENTS.filter(
    ({ key }) => !notNeeded.includes(key)
  );
  const pickedSignatories = toSignatories(picked, notNeeded);
  // At least one department has to sign: the backend never completes an empty readiness.
  const allPicked =
    neededDepartments.length > 0 &&
    pickedSignatories.length === neededDepartments.length;
  const toggleNotNeeded = (department: ReadinessDepartment, value: boolean) =>
    setNotNeeded((prev) =>
      value ? [...prev, department] : prev.filter((d) => d !== department)
    );
  const currentUserRow = signatories.find((s) =>
    sameEmail(s.email, currentUserEmail)
  );
  const canSign = inProgress && !!currentUserRow && !currentUserRow.signed;
  const canWithdraw =
    canManage && readinessEditable && (inProgress || completed);

  const openDialog = () => {
    setRequiredDraft(required);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setRequiredDraft(required);
    setTicked(false);
  };

  const saveAndClose = (next: OperationalReadiness) => {
    onSave(next);
    setDialogOpen(false);
  };

  const handleNeededChange = (checked: boolean) => {
    setRequiredDraft(checked);
    setDialogOpen(true);
  };

  const handleSaveDraft = () =>
    saveAndClose({
      required: requiredDraft,
      status: "draft",
      signatories: pickedSignatories
    });

  const handleSend = () =>
    saveAndClose({
      required: true,
      status: "in_progress",
      signatories: pickedSignatories
    });

  const handleSign = () => {
    if (!readiness) return;
    saveAndClose({
      ...readiness,
      signatories: readiness.signatories.map((s) =>
        sameEmail(s.email, currentUserEmail) && !s.signed
          ? {
              ...s,
              signed: true,
              signedBy: currentUserEmail,
              signedAt: dayjs().toISOString()
            }
          : s
      )
    });
  };

  const handleWithdraw = () => {
    if (!readiness) return;
    saveAndClose({
      ...readiness,
      status: "draft",
      completedAt: null,
      signatories: readiness.signatories.map((s) => ({
        ...s,
        signed: false,
        signedBy: null,
        signedAt: null
      }))
    });
  };

  const status = completed ? (
    <StatusChip label="Completed" color={Colors.NICE_GREEN} />
  ) : inProgress ? (
    <StatusChip
      label={`In progress (${signedCount}/${signatories.length} signed)`}
      color={Colors.MFT_ORANGE}
    />
  ) : required ? (
    <StatusChip label="Not sent" color={Colors.MFT_ORANGE} />
  ) : (
    <StatusChip label="Not needed" />
  );

  return (
    <>
      <Card
        variant="outlined"
        elevation={3}
        onClick={required || canManage ? openDialog : undefined}
        sx={{
          breakInside: "avoid",
          marginBottom: 2,
          cursor: required || canManage ? "pointer" : "default"
        }}
      >
        <CardContent sx={{ padding: 2, "&:last-child": { paddingBottom: 2 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1
            }}
          >
            <Typography variant="h6">Operational readiness</Typography>
            {status}
            {canConfigure && (
              <FormControlLabel
                sx={{ marginLeft: "auto", marginRight: 0 }}
                onClick={(e: MouseEvent) => e.stopPropagation()}
                control={
                  <Checkbox
                    size="small"
                    checked={required}
                    disabled={saving}
                    onChange={(e) => handleNeededChange(e.target.checked)}
                  />
                }
                label="Needed"
              />
            )}
          </Box>
          {required &&
            (signatories.length > 0 || storedNotNeeded.length > 0) && (
              <>
                <Divider sx={{ marginY: 1 }} />
                <Stack spacing={1}>
                  {signatories.map((s) => (
                    <Box
                      key={s.department}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ flex: "0 0 72px" }}
                      >
                        {readinessDepartmentLabel(s.department)}
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {s.name}
                        </Typography>
                        {s.signed && s.signedAt && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="div"
                          >
                            Signed {dayjs(s.signedAt).format("LLL")}
                          </Typography>
                        )}
                      </Box>
                      {s.signed ? (
                        <StatusChip label="Signed" color={Colors.NICE_GREEN} />
                      ) : inProgress ? (
                        <StatusChip
                          label="Awaiting"
                          color={Colors.MFT_ORANGE}
                        />
                      ) : (
                        <StatusChip label="Not sent" />
                      )}
                    </Box>
                  ))}
                  {storedNotNeeded.map((department) => (
                    <Box
                      key={department}
                      sx={{ display: "flex", alignItems: "center", gap: 2 }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ flex: "0 0 72px" }}
                      >
                        {readinessDepartmentLabel(department)}
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <StatusChip label="Not needed" />
                    </Box>
                  ))}
                </Stack>
              </>
            )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Operational readiness</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Risk, Treasury and Finance confirm that their tools and processes
            are set up to trade before the sign off is sent to the VPs. Mark a
            department as not needed to leave it out.
          </Typography>

          {configuring &&
            (canConfigure ? (
              <>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={requiredDraft}
                        disabled={saving}
                        onChange={(e) => setRequiredDraft(e.target.checked)}
                      />
                    }
                    label="Operational readiness needed"
                  />
                </Box>
                {requiredDraft && (
                  <Stack spacing={2} sx={{ marginTop: 1 }}>
                    {READINESS_DEPARTMENTS.map(({ key, label }) => {
                      const skipped = notNeeded.includes(key);
                      return (
                        <Box
                          key={key}
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <ReadinessSignerSelect
                              department={key}
                              label={label}
                              prefill={prefill}
                              disabled={skipped || saving}
                              value={skipped ? null : picked[key]}
                              onChange={(signer) =>
                                setPicked((prev) => ({
                                  ...prev,
                                  [key]: signer
                                }))
                              }
                            />
                          </Box>
                          <FormControlLabel
                            sx={{ marginRight: 0, flexShrink: 0 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={skipped}
                                disabled={saving}
                                onChange={(e) =>
                                  toggleNotNeeded(key, e.target.checked)
                                }
                              />
                            }
                            label="Not needed"
                          />
                        </Box>
                      );
                    })}
                    {!allPicked && (
                      <Typography variant="caption" color="text.secondary">
                        {neededDepartments.length === 0
                          ? "At least one department must sign. Untick Operational readiness needed if none do."
                          : `Pick a signer for ${neededDepartments
                              .map((d) => d.label)
                              .join(", ")} to send.`}
                      </Typography>
                    )}
                  </Stack>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Operational readiness has not been sent yet.
              </Typography>
            ))}

          {!configuring && (
            <>
              {canSign && (
                <Typography variant="body2" color="text.secondary">
                  Check the box next to your name and click the sign button
                </Typography>
              )}
              <List dense>
                {signatories.map((s) => {
                  const isCurrentUser = sameEmail(s.email, currentUserEmail);
                  return (
                    <ListItem key={s.department} disableGutters>
                      <Checkbox
                        checked={s.signed || (isCurrentUser && ticked)}
                        disabled={!(canSign && isCurrentUser)}
                        onChange={(e) => setTicked(e.target.checked)}
                      />
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "center",
                              gap: 1
                            }}
                          >
                            <span>
                              {readinessDepartmentLabel(s.department)}: {s.name}
                            </span>
                            {s.signed ? (
                              <StatusChip
                                label="Signed"
                                color={Colors.NICE_GREEN}
                              />
                            ) : (
                              <Chip
                                size="small"
                                label="Awaiting"
                                color="warning"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          s.signed && s.signedAt
                            ? `${s.email} · signed ${dayjs(s.signedAt).format("LLL")}`
                            : s.email
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
              {storedNotNeeded.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Not needed:{" "}
                  {storedNotNeeded.map(readinessDepartmentLabel).join(", ")}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            {canConfigure || canSign ? "Cancel" : "Close"}
          </Button>
          {canConfigure && (requiredDraft || required) && (
            <Button
              variant="outlined"
              disabled={saving}
              loading={saving}
              onClick={handleSaveDraft}
            >
              Save
            </Button>
          )}
          {canConfigure && requiredDraft && (
            <Button
              variant="contained"
              endIcon={<Send />}
              disabled={!allPicked || saving}
              loading={saving}
              onClick={handleSend}
            >
              Send for operational readiness
            </Button>
          )}
          {canWithdraw && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<Undo />}
              disabled={saving}
              onClick={() => setWithdrawOpen(true)}
            >
              Withdraw
            </Button>
          )}
          {canSign && (
            <Button
              variant="contained"
              disabled={saving || !ticked}
              loading={saving}
              onClick={handleSign}
            >
              Sign
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <WithdrawSignOffDialog
        open={withdrawOpen}
        entityName={entityName}
        saving={saving}
        subject="operational readiness"
        onClose={() => setWithdrawOpen(false)}
        onConfirm={() => {
          setWithdrawOpen(false);
          handleWithdraw();
        }}
      />
    </>
  );
};
